/**
 * Canonical US phone formatting: (xxx) xxx-xxxx.
 *
 * Mirrors App\Helpers\PhoneHelper (twin-laravel) and phone_helper.php
 * (twin-ci). All three write to the same `twin` database, so they must stay
 * in step or each will keep undoing the others' formatting.
 *
 * A field may hold up to MAX_PHONES numbers separated by commas. Anything that
 * is not a recognisable 10 digit NANP number is handed back untouched rather
 * than reshaped, so legacy and partial values survive a round trip intact.
 */

/** Most numbers one field is allowed to hold. Drives the UI repeater. */
export const MAX_PHONES = 2;

/** Digits in a US number. Typing is capped here and validation enforces it. */
export const MAX_DIGITS = 10;

/**
 * Separators that mean "another number follows".
 *
 * Deliberately excludes "/", which appears inside single numbers often enough
 * ("916/481-0268") that splitting on it does more harm than good.
 */
const SEPARATORS = /\s*[,;]\s*|\s+or\s+/i;

const BARE_NUMBER = /^[\d\s().+\-]+$/;
const EXTENSION = /^(.*?)\s*(?:x|ext\.?|extension)\s*(\d+)$/i;

/**
 * Any ten digits are formatted, including area codes that are not valid NANP.
 * Test data routinely uses made up numbers and it has to render the same way
 * real data does.
 */
const TEN_DIGITS = /^\d{10}$/;

/**
 * Digits only, for comparing against whatever the user typed into a search
 * box. Stored values are a mix of formatted and raw, so both sides of a phone
 * search have to be reduced to this before comparing.
 */
export function phoneDigits(value: string): string {
  if (!value) {
    return '';
  }
  return value.replace(/\D+/g, '');
}

/**
 * Split a stored value into its individual formatted numbers.
 *
 * Parts are never dropped, so a legacy value holding more than MAX_PHONES
 * still round trips in full. Pass max only when truncation is intended.
 */
export function phoneParts(value: string, max?: number): string[] {
  if (!value || !value.trim()) {
    return [];
  }

  // Only blank segments are discarded. Dropping segments that merely lack
  // digits would turn "916-481-0268, home" into "(916) 481-0268". formatOne
  // leaves anything it cannot read alone.
  let segments = value.trim().split(SEPARATORS).filter(segment => segment.trim() !== '');

  if (!segments.length) {
    return [];
  }

  if (max != null) {
    segments = segments.slice(0, max);
  }

  return segments.map(formatOne);
}

/** Canonical form of a whole field value. */
export function formatPhone(value: string, max?: number): string {
  return phoneParts(value, max).join(', ');
}

/**
 * Progressive formatting for a field as it is typed into.
 *
 * Caps at ten digits and introduces the parentheses and dash as soon as there
 * are enough digits to need them, so a partial entry still reads as a phone
 * number: "916" -> "(916", "9164" -> "(916) 4", "9164810" -> "(916) 481-0".
 */
export function formatPhoneAsTyped(value: string): string {
  let d = phoneDigits(value);

  // A pasted "+1 916 481 0268" carries the country code. Drop it, or slicing
  // to ten would shunt it into the area code and give "(191) 648-1026".
  if (d.length === 11 && d.charAt(0) === '1') {
    d = d.slice(1);
  }
  d = d.slice(0, MAX_DIGITS);

  return d.length ? groupDigits(d) : '';
}

/**
 * Whether every number in the value is a complete US number.
 *
 * The field masks as you type, so letters and excess digits never survive to
 * be judged here. That leaves two things this actually catches: a number the
 * user stopped typing partway through, and a value stored before the ten
 * digit cap existed - which is shown untouched and reported rather than
 * silently truncated.
 *
 * Emptiness passes: whether the field is mandatory is `required`'s job.
 */
export function isValidPhone(value: string): boolean {
  if (!value || !value.trim()) {
    return true;
  }

  const parts = phoneParts(value);
  if (!parts.length) {
    return false;
  }

  return parts.every(part => {
    if (!BARE_NUMBER.test(part)) {
      return false;
    }
    return phoneDigits(part).length === MAX_DIGITS;
  });
}

/** Format a single number, or hand it back untouched. */
function formatOne(part: string): string {
  part = part.trim();
  let extension = '';

  // Hold any trailing extension aside so it survives formatting.
  const withExtension = part.match(EXTENSION);
  if (withExtension) {
    part = withExtension[1].trim();
    extension = ' x' + withExtension[2];
  }

  // Anything beyond digits and phone punctuation means this is annotated
  // ("9164810268 cell") and reshaping it would drop the annotation.
  if (!BARE_NUMBER.test(part)) {
    return (part + extension).trim();
  }

  let digits = phoneDigits(part);

  if (digits.length === 11 && digits.charAt(0) === '1') {
    digits = digits.substr(1);
  }

  // Over ten digits is not a US number this can shape, so it is left as it is
  // and reported by isValidPhone instead of being quietly truncated. Under
  // four, grouping would leave a dangling "(" which reads as broken data on a
  // page - that only makes sense mid-typing, where the caret explains it.
  if (digits.length < 4 || digits.length > MAX_DIGITS) {
    return (part + extension).trim();
  }

  // Short values are grouped as far as their digits reach, so a stored
  // "132465897" reads as "(132) 465-897" rather than a run of digits.
  return groupDigits(digits) + extension;
}

/** Wraps and dashes as many digits as are present, up to a full number. */
function groupDigits(d: string): string {
  if (d.length <= 3) {
    return '(' + d;
  }
  if (d.length <= 6) {
    return '(' + d.slice(0, 3) + ') ' + d.slice(3);
  }
  return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
}
